// Label vocabulary defaults — the scoped-label taxonomy Loom expects in any
// GitLab project it serves. Projects may override any axis via .loom.yml;
// the values below are the defaults when the file is absent
// or omits a given axis.
//
// All labels are GitLab scoped labels — `<scope>::<value>`. Scoped labels are
// mutually exclusive within their scope, which is exactly the semantics we
// want for workflow state, type, origin, and priority.
//
// Load-bearing rule: the labels listed here are not cosmetic. The workflow
// catalogue (@loom/workflows) reads and writes them. Renaming `workflow::ready`
// to `workflow::up-next` in a project's .loom.yml is fine — the workflow code
// reads the effective config — but adding a brand-new value without teaching
// the workflows about it is a no-op for Loom.

import { z } from "zod";

/** A GitLab scoped label key (the `<scope>` part of `<scope>::<value>`). */
export const LABEL_SCOPES = [
  "workflow",
  "type",
  "origin",
  "priority",
  "risk",
  "meta",
] as const;
export type LabelScope = (typeof LABEL_SCOPES)[number];

/** The scoped-label separator GitLab uses. Centralised so callers can't drift. */
export const LABEL_SEPARATOR = "::";

/**
 * Compose a scoped label string from scope + value.
 * Example: `scopedLabel("workflow", "ready")` → `"workflow::ready"`.
 */
export const scopedLabel = (scope: LabelScope, value: string): string =>
  `${scope}${LABEL_SEPARATOR}${value}`;

// ---------------------------------------------------------------------------
// Loom process markers
//
// Fixed labels Loom writes and reads itself, outside the per-project
// vocabulary. This is their one spelling: every writer and reader imports it.
// ---------------------------------------------------------------------------

/** An issue Loom filed as a proposal (intake, meetings) that no human has
 *  accepted yet. Accept removes it; Decline closes the issue with it still on. */
export const PROPOSAL_PENDING_LABEL = "loom::proposal-pending";

/** An issue that still needs refine before it is ready. Refine apply clears
 *  it once the card passes DoR. */
export const NEEDS_REFINEMENT_LABEL = "loom::needs-refinement";

// ---------------------------------------------------------------------------
// Default values per scope
// ---------------------------------------------------------------------------

/**
 * Workflow state — the Kanban/Scrumban columns. Mutually exclusive.
 *
 * Transitions are gated by lifecycle interceptors:
 * `dor_check` runs before any transition into `ready`; `dod_check`
 * runs before any transition into `done`.
 */
export const DEFAULT_WORKFLOW_LABELS = [
  "discovery",
  "ready",
  "in-progress",
  "review",
  "done",
] as const;
export type DefaultWorkflowLabel = (typeof DEFAULT_WORKFLOW_LABELS)[number];

/**
 * Issue type — the kind of work-item. Mutually exclusive.
 *
 * `decision` and `meeting-digest` and `retrospective` are first-class types
 * because Loom workflows author them (pure GitLab as the
 * store; cross-cutting artifacts need a home as issues).
 */
export const DEFAULT_TYPE_LABELS = [
  "feature",
  "bug",
  "decision",
  "need",
  "retrospective",
  "meeting-digest",
  "spike",
  "chore",
] as const;
export type DefaultTypeLabel = (typeof DEFAULT_TYPE_LABELS)[number];

/**
 * Origin — where the issue came from. Mutually exclusive.
 *
 * `origin::customer` is the entry point for Customer Intake mode; the intake
 * workflow grooms `origin::customer + type::need` into well-formed
 * `type::feature` (or similar) over time, preserving the link for audit.
 */
export const DEFAULT_ORIGIN_LABELS = [
  "customer",
  "internal",
  "regulatory",
] as const;
export type DefaultOriginLabel = (typeof DEFAULT_ORIGIN_LABELS)[number];

/** Priority — coarse ranking. Mutually exclusive. */
export const DEFAULT_PRIORITY_LABELS = ["p0", "p1", "p2", "p3"] as const;
export type DefaultPriorityLabel = (typeof DEFAULT_PRIORITY_LABELS)[number];

/**
 * Risk — coarse delivery-risk signal used by groom and decompose workflows.
 * Mutually exclusive. Optional axis — projects may omit it.
 */
export const DEFAULT_RISK_LABELS = ["low", "medium", "high"] as const;
export type DefaultRiskLabel = (typeof DEFAULT_RISK_LABELS)[number];

/**
 * Meta — Loom-internal markers. Mutually exclusive.
 *
 * `meta::enrichment-ledger` marks the single per-project issue Loom uses to
 * track lazy-enrichment coverage.
 * `meta::quality-rollup` marks the single per-project issue that records
 * `quality_check` rubric verdict aggregates over time.
 * Reserved for Loom's own use; projects should not author these manually.
 */
export const DEFAULT_META_LABELS = [
  "enrichment-ledger",
  "quality-rollup",
] as const;
export type DefaultMetaLabel = (typeof DEFAULT_META_LABELS)[number];

// ---------------------------------------------------------------------------
// Aggregated default vocabulary + Zod schema
// ---------------------------------------------------------------------------

export const DEFAULT_LABEL_VOCABULARY = {
  workflow: DEFAULT_WORKFLOW_LABELS,
  type: DEFAULT_TYPE_LABELS,
  origin: DEFAULT_ORIGIN_LABELS,
  priority: DEFAULT_PRIORITY_LABELS,
  risk: DEFAULT_RISK_LABELS,
  meta: DEFAULT_META_LABELS,
} as const;

/**
 * Zod schema for the `labels:` block in `.loom.yml`. All axes are optional;
 * absent axes fall back to `DEFAULT_LABEL_VOCABULARY`.
 *
 * Each axis value is a non-empty kebab-case-friendly string. We do not
 * enforce a strict character set — GitLab itself accepts most characters in
 * label values, and projects sometimes use existing labels that don't match
 * our taste. The constraint is "non-empty, trimmed."
 */
const labelValueSchema = z
  .string()
  .trim()
  .min(1, "label value must not be empty");

export const LabelVocabularySchema = z
  .object({
    workflow: z.array(labelValueSchema).optional(),
    type: z.array(labelValueSchema).optional(),
    origin: z.array(labelValueSchema).optional(),
    priority: z.array(labelValueSchema).optional(),
    risk: z.array(labelValueSchema).optional(),
    meta: z.array(labelValueSchema).optional(),
  })
  .strict();

export type LabelVocabulary = z.infer<typeof LabelVocabularySchema>;

/**
 * Merge a partial label vocabulary (from .loom.yml) with the defaults.
 * Any axis the user did not supply falls back to the default values.
 */
export const resolveLabelVocabulary = (
  override: LabelVocabulary | undefined,
): Record<LabelScope, readonly string[]> => ({
  workflow: override?.workflow ?? DEFAULT_LABEL_VOCABULARY.workflow,
  type: override?.type ?? DEFAULT_LABEL_VOCABULARY.type,
  origin: override?.origin ?? DEFAULT_LABEL_VOCABULARY.origin,
  priority: override?.priority ?? DEFAULT_LABEL_VOCABULARY.priority,
  risk: override?.risk ?? DEFAULT_LABEL_VOCABULARY.risk,
  meta: override?.meta ?? DEFAULT_LABEL_VOCABULARY.meta,
});
